import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { useDispatch } from 'react-redux'
import { baseClient } from 'builder/modules/apiClient'
import { actions } from 'builder/modules/autoApply/autoApplyModule'
import { useUser } from 'builder/hooks/useUser'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { CareerProfileData } from 'builder/modules/careerProfile'
import { mapCareerResponse } from './mappers'

export const useUploadResume = (
  onUploadProgress?: AxiosRequestConfig['onUploadProgress'],
  onSuccess?: (data: AutoApplyForm) => void,
) => {
  const user = useUser()
  const dispatch = useDispatch()

  const renameFile = useCallback(
    (file: File): File => {
      try {
        const fileName = file.name.replace(/\.[^/.]+$/, '')
        const extension = file.name.split('.').pop() || 'txt'

        const renamedFilename =
          user?.firstName && user?.lastName ? `${user.firstName}_${user.lastName}_Resume` : fileName
        return new File([file], `${renamedFilename}.${extension}`, { type: file.type })
      } catch (error) {
        console.error('Error normalizing filename:', error)
        return file
      }
    },
    [user],
  )

  return useMutation<AutoApplyForm, AxiosError<{ error: { resume: string[] } }>, File, unknown>({
    mutationFn: async (resume: File) => {
      const response = await baseClient.post<CareerProfileData>(
        `/career/v1/profile/upload-resume`,
        {
          resume: renameFile(resume),
        },
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress,
        },
      )
      dispatch(actions.updateForm(response.data))
      return mapCareerResponse(response.data)
    },
    onSuccess,
  })
}
